import React, { useState, useRef, useEffect } from 'react';
import { IoArrowBack, IoClose } from 'react-icons/io5';
import { AiOutlinePlus, AiOutlineEdit } from 'react-icons/ai';
import { BsInfoCircle } from 'react-icons/bs';
import FacultyLabSlotSelectorModal from './FacultyLabSlotSelectorModal';
import SlotSelector from './SlotSelector';
import useConfig from '../config';
import { Faculty } from '../utils/types';

// Define available lab slots
// const EXCLUDED_SLOTS = ['L16', 'L17', 'L18'];
// const MORNING_LAB_SLOTS = Array.from({ length: 30 }, (_, i) => `L${i + 1}`)
//   .filter(slot => !EXCLUDED_SLOTS.includes(slot)); // L1-L30 except L16, L17, L18
// const EVENING_LAB_SLOTS = Array.from({ length: 30 }, (_, i) => `L${i + 31}`); // L31-L60

interface LabSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onForceClose: () => void;
  onSubmit: (facultyLabAssignments: Map<string, string[]>) => void;
  courseName: string;
  theorySlot: string;
  theoryCourseActualSlots: string[];
  facultyPreferences: string[];
  allCurrentlyUsedSlots: string[];
  slotConflictPairs: string[][];
  slotColor: string;
  initialAssignments?: Map<string, string[]>;
  courseType?: string;
  courseCode?: string;
  allFacultyData: Faculty[];
}

const LabSlotModal: React.FC<LabSlotModalProps> = ({
  isOpen,
  onClose,
  onForceClose,
  onSubmit,
  courseName,
  theorySlot,
  theoryCourseActualSlots,
  facultyPreferences,
  allCurrentlyUsedSlots,
  slotConflictPairs,
  slotColor,
  initialAssignments,
  courseType,
  courseCode,
  allFacultyData
}) => {
  const config = useConfig();
  const { MORNING_LAB_SLOTS, EVENING_LAB_SLOTS } = config;

  const labCourseName = `${courseName} Lab`;
  // State to hold faculty-specific lab slots. Key: facultyName, Value: string[] of lab slots
  const [facultySpecificLabSlots, setFacultySpecificLabSlots] = useState<Map<string, string[]>>(new Map());
  const [shakingFaculty, setShakingFaculty] = useState<string[]>([]);

  // State for the pop-up lab slot selector
  const [isFacultyLabSlotPopupOpen, setIsFacultyLabSlotPopupOpen] = useState(false);
  const [popupAnchorElement, setPopupAnchorElement] = useState<HTMLElement | null>(null);
  const [currentFacultyForPopup, setCurrentFacultyForPopup] = useState<string>('');
  const [slotsToOfferInPopup, setSlotsToOfferInPopup] = useState<string[]>([]);
  const [slotSelectorAnchorEl, setSlotSelectorAnchorEl] = useState<HTMLElement | null>(null);

  // Add state for pre-selected slots
  const [preSelectedSlots, setPreSelectedSlots] = useState<string[]>([]);

  const isMorningTheory = theoryCourseActualSlots.some(slot => config.morningTheorySlotsConst.includes(slot));
  
  const determineAvailableLabSlots = (facultyName: string): string[] => {
    if (courseType === 'ETH') {
      const theorySlotString = theoryCourseActualSlots.slice().sort().join('+');
      
      const ethEntryIndex = allFacultyData.findIndex(
        (entry) =>
          entry.CODE === courseCode &&
          entry.FACULTY.toLowerCase() === facultyName.toLowerCase() &&
          entry.TYPE === 'ETH' &&
          entry.SLOT.split('+').sort().join('+') === theorySlotString
      );

      if (ethEntryIndex > -1) {
        const immediateElaEntry = allFacultyData
          .slice(ethEntryIndex + 1)
          .find(
            (entry) =>
              entry.CODE === courseCode &&
              entry.FACULTY.toLowerCase() === facultyName.toLowerCase() &&
              entry.TYPE === 'ELA'
          );

        if (immediateElaEntry) {
          return immediateElaEntry.SLOT.split('+').filter(Boolean);
        }
      }
      return [];
    }
    return isMorningTheory ? EVENING_LAB_SLOTS : MORNING_LAB_SLOTS;
  };

  const handleAssignSlotsClick = (event: React.MouseEvent<HTMLButtonElement>, facultyName: string) => {
    setCurrentFacultyForPopup(facultyName);
    setSlotsToOfferInPopup(determineAvailableLabSlots(facultyName));
    setSlotSelectorAnchorEl(event.currentTarget);
    
    // Pre-select previously chosen slots
    const previouslySelectedSlots = facultySpecificLabSlots.get(facultyName.toLowerCase()) || [];
    if (previouslySelectedSlots.length > 0) {
      // We'll pass these pre-selected slots to the SlotSelector component
      setPreSelectedSlots(previouslySelectedSlots);
    }
  };

  const handleSlotSelectorClose = () => {
    setSlotSelectorAnchorEl(null);
    setCurrentFacultyForPopup('');
    setPreSelectedSlots([]);
  };

  const handleFacultyLabSlotPopupSubmit = (selectedSlots: string[]) => {
    const updatedMap = new Map(facultySpecificLabSlots);
    if (selectedSlots.length > 0) {
      updatedMap.set(currentFacultyForPopup.toLowerCase(), selectedSlots);
    } else {
      updatedMap.delete(currentFacultyForPopup.toLowerCase()); // Remove entry if no slots selected
    }
    setFacultySpecificLabSlots(updatedMap);
    handleSlotSelectorClose();
  };
  
  const mainModalRef = useRef<HTMLDivElement>(null); // Ref for the main modal div

  useEffect(() => {
    if (isOpen) {
      const validLabSlots = isMorningTheory ? EVENING_LAB_SLOTS : MORNING_LAB_SLOTS;
      const filteredAssignments = new Map<string, string[]>();

      if (initialAssignments) {
        initialAssignments.forEach((slots, faculty) => {
          // Keep all slots from initialAssignments, don't filter them
          filteredAssignments.set(faculty.toLowerCase(), slots);
        });
      }

      setFacultySpecificLabSlots(filteredAssignments);

      const facultyPrefModal = document.querySelector('.faculty-preference-modal');
      if (facultyPrefModal) {
        facultyPrefModal.classList.add('lab-modal-open');
      }
    }
    return () => {
      const facultyPrefModal = document.querySelector('.faculty-preference-modal');
      if (facultyPrefModal) {
        facultyPrefModal.classList.remove('lab-modal-open');
      }
    };
  }, [isOpen, initialAssignments, isMorningTheory, MORNING_LAB_SLOTS, EVENING_LAB_SLOTS]);

  if (!isOpen) return null;

  const isConfirmLabDetailsDisabled = facultyPreferences.length > 0 &&
    !facultyPreferences.every(facultyName => {
        const assignedSlots = facultySpecificLabSlots.get(facultyName.toLowerCase());
        return assignedSlots && assignedSlots.length > 0;
    });

  const handleConfirmLabDetails = () => {
    if (isConfirmLabDetailsDisabled) {
      const unassignedFaculty = facultyPreferences.filter(facultyName => {
        const assignedSlots = facultySpecificLabSlots.get(facultyName.toLowerCase());
        return !assignedSlots || assignedSlots.length === 0;
      });
      setShakingFaculty(unassignedFaculty);
      setTimeout(() => setShakingFaculty([]), 820);
      return;
    }
    onSubmit(facultySpecificLabSlots);
    onClose();  // Add this line to close the modal after submission
  };

  return (
    <div ref={mainModalRef} className="fixed inset-0 flex items-center justify-center z-[60]">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm"></div>
      <div className={`bg-white rounded-2xl shadow-xl z-10 relative p-8 pb-6 ${
        facultyPreferences.length > 5 
          ? 'w-[900px]' 
          : 'w-[550px]'
      }`}>
        {/* New header with Back button on left and X button on right */}
        <div className="flex justify-between items-center -mt-2 mb-6">
          <button 
            onClick={onClose}
            className="text-gray-600 hover:text-black transition-colors flex items-center"
            aria-label="Back to faculty preferences"
          >
            <IoArrowBack size={24} />
          </button>
          <button 
            onClick={onForceClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close modal"
          >
            <IoClose size={24} />
          </button>
        </div>

        <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center break-words">Lab Slots for "{labCourseName}"</h2>
        
        <p className="text-center mb-8 text-gray-600 flex items-center justify-center gap-3">
          Theory slot: <span className={`inline-block px-3 py-1 text-sm font-medium rounded-md ${slotColor}`}>
            {theorySlot}
          </span>
          <span className="flex items-center space-x-1 bg-gray-100 rounded-lg px-2 py-1">
            <span className="text-xs text-gray-500">Credits:</span>
            <span className="text-sm font-medium text-gray-900">1</span>
          </span>
        </p>
        
        {facultyPreferences && facultyPreferences.length > 0 ? (
          <div className="flex mb-8 gap-x-6">
            {/* Left Column */}
            <div className="flex-1">
              <div className="px-2">
                {facultyPreferences.slice(0, 5).map((faculty, index) => {
                  const assignedSlotsArray = facultySpecificLabSlots.get(faculty.toLowerCase());
                  const assignedSlotsString = assignedSlotsArray && assignedSlotsArray.length > 0 ? assignedSlotsArray.join('+') : null;
                  return (
                    <div key={index} className={`flex items-center relative transition-all duration-200 mb-5 ${shakingFaculty.includes(faculty) ? 'animate-shake' : ''}`}>
                      <div className="flex items-center justify-center h-9 w-9 rounded-full bg-blue-100 text-blue-700 font-medium mr-3">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="relative py-3 px-4 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors group">
                          <div className="flex justify-between items-center">
                            <span className="break-words text-[16px] leading-[24px]">{faculty}</span>
                            {assignedSlotsString && (
                              <div className={`text-xs px-2.5 py-1.5 ml-3 rounded-md font-medium break-words ${
                                index === 0 ? slotColor : 'bg-gray-50 text-gray-700'
                              }`}>
                                {assignedSlotsString}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleAssignSlotsClick(e, faculty)}
                        className="ml-3 text-gray-500 hover:text-black transition-transform hover:scale-110"
                      >
                        {assignedSlotsString ? <AiOutlineEdit size={24} /> : <AiOutlinePlus size={24} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column */}
            {facultyPreferences.length > 5 && (
              <div className="flex-1">
                <div className="px-2">
                  {facultyPreferences.slice(5).map((faculty, index) => {
                    const assignedSlotsArray = facultySpecificLabSlots.get(faculty.toLowerCase());
                    const assignedSlotsString = assignedSlotsArray && assignedSlotsArray.length > 0 ? assignedSlotsArray.join('+') : null;
                    return (
                      <div key={index} className={`flex items-center relative transition-all duration-200 mb-5 ${shakingFaculty.includes(faculty) ? 'animate-shake' : ''}`}>
                        <div className="flex items-center justify-center h-9 w-9 rounded-full bg-blue-100 text-blue-700 font-medium mr-3">
                          {index + 6}
                        </div>
                        <div className="flex-1">
                          <div className="relative py-3 px-4 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors group">
                            <div className="flex justify-between items-center">
                              <span className="break-words text-[16px] leading-[24px]">{faculty}</span>
                              {assignedSlotsString && (
                                <div className="text-xs bg-gray-50 text-gray-700 px-2.5 py-1.5 ml-3 rounded-md font-medium break-words">
                                  {assignedSlotsString}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleAssignSlotsClick(e, faculty)}
                          className="ml-3 text-gray-500 hover:text-black transition-transform hover:scale-110"
                        >
                          {assignedSlotsString ? <AiOutlineEdit size={24} /> : <AiOutlinePlus size={24} />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
           <p className="text-gray-600 mb-6 text-center px-2 break-words">
             No faculty preferences were set for the theory course.
           </p>
        )}

        <div className="flex items-center justify-center gap-1.5 mb-3">
          <p className="text-sm text-gray-700 text-center">
            First Lab slot blocked; others marked red/green by availability.
          </p>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleConfirmLabDetails}
            className={`confirm-btn px-6 py-3 rounded-lg text-sm font-medium text-white transition-colors ${
              isConfirmLabDetailsDisabled
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-black'
            }`}
          >
            Confirm Lab Details
          </button>
        </div>
      </div>
      
      {/* Faculty Lab Slot Selector Pop-up */}
      <SlotSelector
        isOpen={Boolean(slotSelectorAnchorEl)}
        onClose={handleSlotSelectorClose}
        onSelect={(selectedSlots) => {
          handleFacultyLabSlotPopupSubmit(selectedSlots);
          handleSlotSelectorClose();
        }}
        referenceElement={slotSelectorAnchorEl}
        placement="right-start"
        slotsToOffer={slotsToOfferInPopup}
        isMorningTheory={isMorningTheory}
        isFirstFaculty={facultyPreferences.length > 0 && currentFacultyForPopup === facultyPreferences[0]}
        blockedSlots={allCurrentlyUsedSlots}
      />
    </div>
  );
};

export default LabSlotModal; 